// The Files tab: every file of the open folder and, when the folder is a git repository, its
// commits, what each one changed, and the history of one file. Git is read in the git worker
// from the folder's `.git` (or a one-time copy of it); nothing is ever written to it.
import type { FileChange, GitCommit, GitHead, GitRef } from '@modelviewer/core';
import { create } from 'zustand';
import { fileContentOf, type FileContent } from '../files/content';
import type { LocalFolderSource } from '../sources/localFolder';
import type { UploadSource } from '../sources/upload';
import type { ChangeCounts, GitOpen } from '../workers/git.worker';
import { gitWorker } from '../workers/gitClient';
import { useWorkspace } from './workspace';

const PAGE = 100;

export type RepoState =
  | { state: 'idle' }
  | { state: 'loading' }
  | GitOpen
  /** The built-in sample: its files, without git. */
  | { state: 'sample' };

export interface FilesState {
  repo: RepoState;
  head: GitHead | null;
  refs: GitRef[];
  /** What the commit list starts from: `HEAD` or a ref name. */
  start: string;
  /** The commit `start` pointed at when the list was read. */
  tip: string | null;
  commits: GitCommit[];
  /** Whether the list holds every commit there is. */
  complete: boolean;
  loading: boolean;
  /** The file whose history the commit list shows, if any. */
  history: string | null;
  /** Commits looked through for that history so far. */
  scanned: number;
  /** The selected commit. */
  commit: string | null;
  /** What the selected commit changed (null while reading it). */
  changes: FileChange[] | null;
  counts: ChangeCounts;
  /** The selected file. */
  file: string | null;
  /** The files of a folder without git. */
  folderFiles: string[] | null;
}

const initial: FilesState = {
  repo: { state: 'idle' },
  head: null,
  refs: [],
  start: 'HEAD',
  tip: null,
  commits: [],
  complete: false,
  loading: false,
  history: null,
  scanned: 0,
  commit: null,
  changes: null,
  counts: {},
  file: null,
  folderFiles: null,
};

export const useFiles = create<FilesState>(() => initial);

/** Bumped whenever the commit list starts over, so answers for an older list are dropped. */
let generation = 0;

const set = useFiles.setState;
const get = useFiles.getState;

function folderSource(): LocalFolderSource | null {
  const source = useWorkspace.getState().source;
  return source?.kind === 'folder' ? (source as LocalFolderSource) : null;
}

function copySource(): UploadSource | null {
  const source = useWorkspace.getState().source;
  return source?.kind === 'upload' ? (source as UploadSource) : null;
}

/** Reads the folder's git repository (once per folder), or its files when there is none. */
export async function openRepo(): Promise<void> {
  const source = useWorkspace.getState().source;
  if (!source || get().repo.state !== 'idle') return;
  set({ repo: { state: 'loading' } });
  const folder = folderSource();
  const copy = copySource();
  if (!folder && !copy) {
    set({ repo: { state: 'sample' }, folderFiles: useWorkspace.getState().files.map((f) => f.path) });
    return;
  }
  const opened = folder ? await gitWorker().open(folder.root) : await gitWorker().openCopy(copy!.git.folder, copy!.git.pointer);
  if (useWorkspace.getState().source !== source) return;
  if (opened.state === 'ready') {
    set({ repo: opened, head: opened.head, refs: opened.refs, start: 'HEAD' });
    await loadCommits(true);
  } else {
    const folderFiles = folder ? await gitWorker().folderPaths() : copy!.paths();
    if (useWorkspace.getState().source !== source) return;
    set({ repo: opened, folderFiles });
  }
}

/** Reads the commit list from the start (`reset`) or its next page. */
async function loadCommits(reset: boolean): Promise<void> {
  // Whether the reader was on the newest commit, to follow it to the new newest one.
  const previous = get().commit;
  const wasNewest = !previous || previous === get().commits[0]?.id;
  if (reset) {
    generation++;
    set({ commits: [], complete: false, scanned: 0, loading: false });
  }
  const mine = generation;
  const before = get();
  if (before.loading && !reset) return;
  set({ loading: true });
  let tip = before.tip;
  if (reset) {
    const now = await gitWorker().tip(before.start);
    if (mine !== generation) return;
    tip = now?.id ?? null;
    set({ tip, ...(now ? { head: now.head } : {}) });
  }
  if (!tip) {
    set({ commits: [], complete: true, loading: false, commit: null, changes: [] });
    return;
  }
  // A file's history: keep looking until something turns up or the history ends.
  for (;;) {
    const s = get();
    const page = s.history ? await gitWorker().history(tip, s.history, s.commits.length, PAGE) : { ...(await gitWorker().log(tip, s.commits.length, PAGE)), scanned: 0 };
    if (mine !== generation) return;
    set((st) => ({ commits: [...st.commits, ...page.commits], complete: page.done, scanned: page.scanned }));
    if (page.commits.length || page.done || !s.history) break;
  }
  set({ loading: false });
  if (reset) {
    const { commits } = get();
    // Keep an older commit the reader picked; follow the newest one otherwise.
    const keep = !wasNewest && commits.some((c) => c.id === previous) ? previous : (commits[0]?.id ?? null);
    if (keep) await selectCommit(keep);
    else set({ commit: null, changes: [] });
  }
}

/** Reads the next page of commits (at the end of the list). */
export function loadMoreCommits(): void {
  const s = get();
  if (!s.loading && !s.complete && s.repo.state === 'ready') void loadCommits(false);
}

export async function selectCommit(id: string): Promise<void> {
  set({ commit: id, changes: null, counts: {} });
  const changes = await gitWorker().changes(id);
  if (get().commit !== id) return;
  let file = get().file;
  // Outside a file's history, show the first change unless the file in view changed here too.
  if (!get().history && changes.length && !changes.some((c) => c.path === file || c.oldPath === file)) file = changes[0]!.path;
  set({ changes, file });
  const counts = await gitWorker().counts(changes);
  if (get().commit === id) set({ counts });
}

export function selectFile(path: string | null): void {
  set({ file: path });
}

/** Lists the commits of another branch or tag (`HEAD` for what is checked out). */
export async function setStart(name: string): Promise<void> {
  if (name === get().start) return;
  set({ start: name, commit: null });
  await loadCommits(true);
}

/** Lists only the commits that changed a file (null: every commit again). */
export async function showHistory(path: string | null): Promise<void> {
  if (path === get().history) return;
  set({ history: path, ...(path ? { file: path } : {}) });
  if (get().repo.state === 'ready') await loadCommits(true);
}

/** Brings a file into view in the Files tab, optionally with its history. */
export async function revealFile(path: string, options: { history?: boolean } = {}): Promise<void> {
  set({ file: path });
  if (options.history) await showHistory(path);
}

/** Reads the branch list again (for the branch menu). */
export async function refreshRefs(): Promise<void> {
  const now = await gitWorker().refs();
  if (now) set({ refs: now.refs, head: now.head });
}

/** Looks for new commits (git committed, fetched or switched branch) and reads the list again when there are. */
export async function checkForCommits(): Promise<void> {
  const s = get();
  // A copy does not change.
  if (s.repo.state !== 'ready' || s.loading || !useWorkspace.getState().source?.live) return;
  const now = await gitWorker().tip(s.start);
  if (!now) return;
  const headMoved = now.head.id !== s.head?.id || now.head.ref !== s.head?.ref;
  if (now.id === s.tip && !headMoved) return;
  await refreshRefs();
  if (now.id !== s.tip) await loadCommits(true);
}

/** A file as it is in a commit, or in the folder itself (commit null). */
export async function fileContent(commit: string | null, path: string): Promise<FileContent> {
  if (commit) return gitWorker().file(commit, path);
  if (folderSource()) return gitWorker().folderFile(path);
  const copy = copySource();
  if (copy) return fileContentOf(await copy.file(path));
  const source = useWorkspace.getState().source;
  if (!source) return { kind: 'missing' };
  try {
    return { kind: 'text', text: await source.read(path) };
  } catch {
    return { kind: 'missing' };
  }
}

/** The two sides of a change: the file before and after the commit. */
export async function changeContents(change: FileChange): Promise<[FileContent, FileContent]> {
  const worker = gitWorker();
  return Promise.all([change.oldId ? worker.blob(change.oldId) : Promise.resolve<FileContent>({ kind: 'text', text: '' }), change.newId ? worker.blob(change.newId) : Promise.resolve<FileContent>({ kind: 'text', text: '' })]);
}

/** Every file path in a commit (for the full file tree). */
export function commitPaths(commit: string): Promise<string[]> {
  return gitWorker().paths(commit);
}

// Another folder starts over.
useWorkspace.subscribe((s, prev) => {
  if (s.source === prev.source) return;
  generation++;
  set({ ...initial });
  if (prev.source?.kind === 'folder') void gitWorker().close();
});
